import type {WorldCoordinate} from '../../../model/ProjectionResult';
import type {
	TopologyFittingLocalPoint2,
	TopologyFittingVectorTuple,
	TopologyFittingPlaneFrame,
	TopologyCircleFitResult,
	TopologyUnitConicCoefficients,
	TopologyEllipseFitResult,
	TopologyQuadraticYFitResult,
	TopologyAngleTraceResult,
} from '../../../../../../types/reference/sketch-entity-reference/TopologySampledCurveFittingMathService';

export type {
	TopologyFittingLocalPoint2,
	TopologyFittingVectorTuple,
	TopologyFittingPlaneFrame,
	TopologyCircleFitResult,
	TopologyUnitConicCoefficients,
	TopologyEllipseFitResult,
	TopologyQuadraticYFitResult,
	TopologyAngleTraceResult,
} from '../../../../../../types/reference/sketch-entity-reference/TopologySampledCurveFittingMathService';

/**
 * sampled-only 曲线拟合数学服务。
 *
 * 说明：
 * 1. 该服务只提供无状态数值计算，不处理 CAD 语义；
 * 2. 高层拟合顺序、descriptor 更新、诊断输出由业务服务决定；
 * 3. 单独封装后，P2-B 后续可替换更强的拟合算法而不改重建主链路。
 */
export class TopologySampledCurveFittingMathService {
	/** 数值计算退化阈值。 */
	public readonly epsilon = 1e-9;

	/**
	 * 规范化采样点。
	 *
	 * @param sampledPointWorlds 原始世界坐标采样点。
	 * @returns 复制后的有效采样点。
	 */
	public normalizeSampledPoints(sampledPointWorlds: WorldCoordinate[]): WorldCoordinate[] {
		return sampledPointWorlds
			.filter((point) => point.length === 3 && point.every(Number.isFinite))
			.map((point): WorldCoordinate => [point[0], point[1], point[2]]);
	}

	/**
	 * 统计去重后的点数量。
	 *
	 * @param points 世界坐标采样点。
	 * @returns 有效不同点数量。
	 */
	public countDistinctPoints(points: WorldCoordinate[]): number {
		const distinct: WorldCoordinate[] = [];
		points.forEach((point) => {
			if (!distinct.some((existing) => this.distance(existing, point) <= this.epsilon)) {
				distinct.push(point);
			}
		});
		return distinct.length;
	}

	/**
	 * 解析直线端点。
	 *
	 * @param points 世界坐标采样点。
	 * @returns 可用端点。
	 */
	public resolveLineEndpoints(points: WorldCoordinate[]): {start: WorldCoordinate; end: WorldCoordinate} | null {
		const start = points[0];
		if (!start) {
			return null;
		}

		const end = [...points].reverse()
			.find((point) => this.distance(start, point) > this.epsilon);
		return end
			? {start, end}
			: null;
	}

	/**
	 * 创建最佳拟合平面。
	 *
	 * @param points 世界坐标采样点。
	 * @returns 可用平面；全部共线时返回 null。
	 */
	public createPlaneFrame(points: WorldCoordinate[]): TopologyFittingPlaneFrame | null {
		const origin = this.centroid(points);
		let normal: TopologyFittingVectorTuple | null = null;
		let maxArea = 0;

		points.forEach((first) => {
			points.forEach((second) => {
				const cross = this.cross(this.subtract(first, origin), this.subtract(second, origin));
				const area = this.length(cross);
				if (area > maxArea) {
					maxArea = area;
					normal = this.scale(cross, 1 / area);
				}
			});
		});

		const resolvedNormal = normal;
		if (!resolvedNormal) {
			return null;
		}

		const preferredXAxis = this.resolvePreferredPlaneXAxis(points, origin);
		const xAxis = this.normalize(this.subtract(preferredXAxis, this.scale(resolvedNormal, this.dot(preferredXAxis, resolvedNormal))));
		const yAxis = this.normalize(this.cross(resolvedNormal, xAxis));
		const maxPlaneDistance = Math.max(...points.map((point) => Math.abs(this.dot(this.subtract(point, origin), resolvedNormal))));

		return {
			origin,
			xAxis,
			yAxis,
			normal: resolvedNormal,
			maxPlaneDistance,
		};
	}

	/**
	 * 让开放圆弧拟合平面严格通过首尾拓扑点。
	 *
	 * 说明：
	 * 1. 最佳拟合平面允许采样点存在微小法向残差，但 Convert Entities 的共享端点
	 *    使用首尾 canonical vertex，解析圆弧平面必须包含这两个硬边界；
	 * 2. 以首点为原点、首尾弦为 X 轴，再把原拟合法向正交化，可保留原平面分支
	 *    并让首尾点的局部法向距离严格为零；
	 * 3. 新平面的全点最大距离会重新计算，调用方仍按原有阈值拒绝不可信拟合。
	 *
	 * @param points 开放圆弧的世界采样点。
	 * @param initialFrame 全点最佳拟合平面。
	 * @returns 通过首尾点的右手平面坐标系；端点或法向退化时返回 `null`。
	 */
	public constrainOpenArcPlaneToEndpoints(
		points: WorldCoordinate[],
		initialFrame: TopologyFittingPlaneFrame,
	): TopologyFittingPlaneFrame | null {
		const start = points[0];
		const end = points[points.length - 1];
		if (!start || !end) {
			return null;
		}

		// 拓扑首尾弦是开放圆弧平面的硬 X 轴；零长弦无法定义稳定平面。
		const chord = this.subtract(end, start);
		const chordLength = this.length(chord);
		if (chordLength <= this.epsilon) {
			return null;
		}
		const xAxis = this.scale(chord, 1 / chordLength);

		// 从原拟合分支中移除沿弦分量，避免任意重选法向导致圆弧方向翻转。
		const projectedNormal = this.subtract(
			initialFrame.normal,
			this.scale(xAxis, this.dot(initialFrame.normal, xAxis)),
		);
		const projectedNormalLength = this.length(projectedNormal);
		if (projectedNormalLength <= this.epsilon) {
			return null;
		}
		const normal = this.scale(projectedNormal, 1 / projectedNormalLength);
		const yAxis = this.normalize(this.cross(normal, xAxis));

		// 以首点为原点后首尾点天然位于平面内，其余采样仍参与可信度校验。
		const origin: TopologyFittingVectorTuple = [start[0], start[1], start[2]];
		const maxPlaneDistance = Math.max(...points.map(point => (
			Math.abs(this.dot(this.subtract(point, origin), normal))
		)));
		return {origin, xAxis, yAxis, normal, maxPlaneDistance};
	}

	/**
	 * 投影世界坐标到平面局部。
	 *
	 * @param point 世界坐标点。
	 * @param frame 平面坐标系。
	 * @returns 局部二维点。
	 */
	public projectToPlane(point: WorldCoordinate, frame: TopologyFittingPlaneFrame): TopologyFittingLocalPoint2 {
		const relative = this.subtract(point, frame.origin);
		return {
			x: this.dot(relative, frame.xAxis),
			y: this.dot(relative, frame.yAxis),
		};
	}

	/**
	 * 平面局部坐标转世界坐标。
	 *
	 * @param point 局部二维点。
	 * @param frame 平面坐标系。
	 * @returns 世界坐标。
	 */
	public localToWorld(point: TopologyFittingLocalPoint2, frame: TopologyFittingPlaneFrame): WorldCoordinate {
		return [
			frame.origin[0] + frame.xAxis[0] * point.x + frame.yAxis[0] * point.y,
			frame.origin[1] + frame.xAxis[1] * point.x + frame.yAxis[1] * point.y,
			frame.origin[2] + frame.xAxis[2] * point.x + frame.yAxis[2] * point.y,
		];
	}

	/**
	 * 最小二乘拟合圆。
	 *
	 * @param points 局部二维采样点。
	 * @returns 圆心、半径和残差。
	 */
	public fitCircle(points: TopologyFittingLocalPoint2[]): TopologyCircleFitResult | null {
		const normalization = this.createLocalPointNormalization(points);
		if (!normalization) {
			return null;
		}
		const normalizedPoints = points.map((point) => this.normalizeLocalPoint(point, normalization));
		const rows = normalizedPoints.map((point) => [point.x, point.y, 1]);
		const values = normalizedPoints.map((point) => -(point.x * point.x + point.y * point.y));
		const solution = this.solveLeastSquares(rows, values);
		if (!solution) {
			return null;
		}

		const [d, e, f] = solution;
		const normalizedCenterX = -d / 2;
		const normalizedCenterY = -e / 2;
		const radiusSquared = normalizedCenterX * normalizedCenterX + normalizedCenterY * normalizedCenterY - f;
		if (radiusSquared <= this.epsilon) {
			return null;
		}

		const normalizedRadius = Math.sqrt(radiusSquared);
		const normalizedMaxResidual = Math.max(...normalizedPoints.map((point) => (
			Math.abs(Math.hypot(point.x - normalizedCenterX, point.y - normalizedCenterY) - normalizedRadius)
		)));
		const centerX = normalization.origin.x + normalizedCenterX / normalization.scale;
		const centerY = normalization.origin.y + normalizedCenterY / normalization.scale;
		const radius = normalizedRadius / normalization.scale;
		const maxResidual = normalizedMaxResidual / normalization.scale;
		return {centerX, centerY, radius, maxResidual};
	}

	/**
	 * 将开放圆弧的最小二乘圆收口到首尾拓扑点。
	 *
	 * 说明：
	 * 1. topology-runtime 的采样点使用 Float32，普通最小二乘圆通常不会严格通过首尾点；
	 * 2. Convert Entities 会按 canonical vertex 合并相邻目标端点，同一点随后必须同时满足
	 *    相邻直线与圆弧方程，因此开放圆弧必须把首尾拓扑点视为硬边界；
	 * 3. 圆心在首尾弦的垂直平分线上时，两端到圆心的距离天然相等；这里把最小二乘圆心
	 *    正交投影到该直线上，在保持全局拟合分支的同时恢复端点一致性；
	 * 4. 调用方仍需使用返回的 `maxResidual` 执行既有拟合阈值校验。
	 *
	 * @param points 开放圆弧的局部二维采样点。
	 * @param initialFit 全点最小二乘圆。
	 * @returns 严格通过首尾点的圆拟合；端点退化时返回 `null`。
	 */
	public constrainOpenArcCircleToEndpoints(
		points: TopologyFittingLocalPoint2[],
		initialFit: TopologyCircleFitResult,
	): TopologyCircleFitResult | null {
		const start = points[0];
		const end = points[points.length - 1];
		if (!start || !end) {
			return null;
		}

		// 首尾弦必须能定义垂直平分线；退化端点交由上层回退其它曲线表达。
		const chordX = end.x - start.x;
		const chordY = end.y - start.y;
		const chordLength = Math.hypot(chordX, chordY);
		if (chordLength <= this.epsilon) {
			return null;
		}

		const midpointX = (start.x + end.x) * 0.5;
		const midpointY = (start.y + end.y) * 0.5;
		const bisectorX = -chordY / chordLength;
		const bisectorY = chordX / chordLength;
		// 保留最小二乘圆心沿垂直平分线的分支，只消除导致两端半径不等的弦向误差。
		const centerOffset = (initialFit.centerX - midpointX) * bisectorX
			+ (initialFit.centerY - midpointY) * bisectorY;
		const centerX = midpointX + bisectorX * centerOffset;
		const centerY = midpointY + bisectorY * centerOffset;
		const radius = Math.hypot(start.x - centerX, start.y - centerY);
		if (!Number.isFinite(radius) || radius <= this.epsilon) {
			return null;
		}

		// 端点收口后重新计算全点误差，禁止以拓扑一致性为由绕过拟合可信度阈值。
		const maxResidual = Math.max(...points.map(point => (
			Math.abs(Math.hypot(point.x - centerX, point.y - centerY) - radius)
		)));
		return {centerX, centerY, radius, maxResidual};
	}

	/**
	 * 拟合单位右侧二次曲线方程。
	 *
	 * @param points 局部二维采样点。
	 * @returns conic 系数。
	 */
	public fitUnitConic(points: TopologyFittingLocalPoint2[]): TopologyUnitConicCoefficients | null {
		const rows = points.map((point) => [point.x * point.x, point.x * point.y, point.y * point.y, point.x, point.y]);
		const values = points.map(() => 1);
		const solution = this.solveLeastSquares(rows, values);
		if (!solution) {
			return null;
		}

		const [a, b, c, d, e] = solution;
		return {a, b, c, d, e, f: -1};
	}

	/**
	 * 从 conic 系数解析椭圆。
	 *
	 * @param conic conic 系数。
	 * @param points 局部二维采样点。
	 * @returns 椭圆参数。
	 */
	public resolveEllipseFromConic(
		conic: TopologyUnitConicCoefficients,
		points: TopologyFittingLocalPoint2[],
	): TopologyEllipseFitResult | null {
		const discriminant = conic.b * conic.b - 4 * conic.a * conic.c;
		if (discriminant >= -this.epsilon) {
			return null;
		}

		const centerDenominator = 4 * conic.a * conic.c - conic.b * conic.b;
		if (Math.abs(centerDenominator) <= this.epsilon) {
			return null;
		}

		const centerX = (conic.b * conic.e - 2 * conic.c * conic.d) / centerDenominator;
		const centerY = (conic.b * conic.d - 2 * conic.a * conic.e) / centerDenominator;
		const centerValue = this.evaluateConic(conic, {x: centerX, y: centerY});
		const eigen = this.resolveSymmetricEigen(conic.a, conic.b / 2, conic.c);
		if (!eigen || centerValue >= 0 || eigen.firstValue <= 0 || eigen.secondValue <= 0) {
			return null;
		}

		const firstRadius = Math.sqrt(-centerValue / eigen.firstValue);
		const secondRadius = Math.sqrt(-centerValue / eigen.secondValue);
		const useFirstAsMajor = firstRadius >= secondRadius;
		const radiusX = useFirstAsMajor ? firstRadius : secondRadius;
		const radiusY = useFirstAsMajor ? secondRadius : firstRadius;
		const majorAxis = useFirstAsMajor ? eigen.firstVector : eigen.secondVector;
		/*
		 * 对称矩阵的特征向量允许任意正负号，独立采用第二条特征向量可能形成左手坐标系。
		 * 椭圆旋转角只由长轴表达，后续重建默认短轴是长轴逆时针旋转九十度，因此必须在这里统一方向。
		 */
		const minorAxis = {x: -majorAxis.y, y: majorAxis.x};
		const maxResidual = Math.max(...points.map((point) => Math.abs(this.evaluateConic(conic, point))));

		if (!Number.isFinite(radiusX) || !Number.isFinite(radiusY)) {
			return null;
		}

		return {
			centerX,
			centerY,
			radiusX,
			radiusY,
			majorAxis,
			minorAxis,
			rotation: Math.atan2(majorAxis.y, majorAxis.x),
			maxResidual,
		};
	}

	/**
	 * 通过归一化局部坐标拟合椭圆。
	 *
	 * 设计说明：
	 * 1. 小尺寸圆角的局部坐标可能只有 1e-5 量级，直接进入正规方程会让二次项接近求解器退化阈值；
	 * 2. 这里先把点集平移并等比缩放到单位尺度求解，再把中心和半径还原回原始局部坐标；
	 * 3. 归一化只影响数值稳定性，不改变最终写入草图引用的世界几何。
	 *
	 * @param points 局部二维采样点。
	 * @returns 椭圆拟合参数。
	 */
	public fitEllipse(points: TopologyFittingLocalPoint2[]): TopologyEllipseFitResult | null {
		const normalization = this.createLocalPointNormalization(points);
		if (!normalization) {
			return null;
		}

		const normalizedPoints = points.map((point) => this.normalizeLocalPoint(point, normalization));
		const conic = this.fitUnitConic(normalizedPoints);
		const ellipse = conic ? this.resolveEllipseFromConic(conic, normalizedPoints) : null;
		if (!ellipse) {
			return null;
		}

		return {
			...ellipse,
			centerX: normalization.origin.x + ellipse.centerX / normalization.scale,
			centerY: normalization.origin.y + ellipse.centerY / normalization.scale,
			radiusX: ellipse.radiusX / normalization.scale,
			radiusY: ellipse.radiusY / normalization.scale,
		};
	}

	/**
	 * 最小二乘拟合 y = ax^2 + bx + c。
	 *
	 * @param points 局部二维采样点。
	 * @returns 二次函数系数。
	 */
	public fitQuadraticY(points: TopologyFittingLocalPoint2[]): TopologyQuadraticYFitResult | null {
		const rows = points.map((point) => [point.x * point.x, point.x, 1]);
		const values = points.map((point) => point.y);
		const solution = this.solveLeastSquares(rows, values);
		return solution
			? {a: solution[0]!, b: solution[1]!, c: solution[2]!}
			: null;
	}

	/**
	 * 计算圆弧角度轨迹。
	 *
	 * @param points 局部点。
	 * @param frame 圆心与局部轴。
	 * @returns 起点为 0 的终止角与方向。
	 */
	public resolveAngleTrace(
		points: TopologyFittingLocalPoint2[],
		frame: {
			centerX: number;
			centerY: number;
			xAxis: TopologyFittingLocalPoint2;
			yAxis: TopologyFittingLocalPoint2;
		},
	): TopologyAngleTraceResult {
		const angles = points.map((point) => {
			const relative = {x: point.x - frame.centerX, y: point.y - frame.centerY};
			return Math.atan2(
				relative.x * frame.yAxis.x + relative.y * frame.yAxis.y,
				relative.x * frame.xAxis.x + relative.y * frame.xAxis.y,
			);
		});
		return this.unwrapAngleTrace(angles);
	}

	/**
	 * 计算椭圆弧参数角轨迹。
	 *
	 * @param points 局部点。
	 * @param ellipse 椭圆参数。
	 * @returns 起点为 0 的终止角与方向。
	 */
	public resolveEllipseAngleTrace(
		points: TopologyFittingLocalPoint2[],
		ellipse: Pick<TopologyEllipseFitResult, 'centerX' | 'centerY' | 'radiusX' | 'radiusY' | 'majorAxis' | 'minorAxis'>,
	): TopologyAngleTraceResult {
		const angles = points.map((point) => {
			const relative = {x: point.x - ellipse.centerX, y: point.y - ellipse.centerY};
			const major = (relative.x * ellipse.majorAxis.x + relative.y * ellipse.majorAxis.y) / ellipse.radiusX;
			const minor = (relative.x * ellipse.minorAxis.x + relative.y * ellipse.minorAxis.y) / ellipse.radiusY;
			return Math.atan2(minor, major);
		});
		return this.unwrapAngleTrace(angles);
	}

	/**
	 * 解析圆 / 圆弧源平面 X 轴。
	 *
	 * @param startWorld 起点世界坐标。
	 * @param centerWorld 圆心世界坐标。
	 * @param fallback 兜底轴。
	 * @returns 单位世界轴。
	 */
	public resolveRadialAxisWorld(
		startWorld: WorldCoordinate,
		centerWorld: WorldCoordinate,
		fallback: TopologyFittingVectorTuple,
	): TopologyFittingVectorTuple {
		const radial = this.subtract(startWorld, centerWorld);
		return this.length(radial) > this.epsilon
			? this.normalize(radial)
			: fallback;
	}

	/**
	 * 计算点到直线距离。
	 *
	 * @param point 当前点。
	 * @param lineStart 直线起点。
	 * @param axis 直线方向。
	 * @param axisLength 方向长度。
	 * @returns 点线距离。
	 */
	public pointLineDistance(
		point: WorldCoordinate,
		lineStart: WorldCoordinate,
		axis: TopologyFittingVectorTuple,
		axisLength: number,
	): number {
		return this.length(this.cross(this.subtract(point, lineStart), axis)) / axisLength;
	}

	/**
	 * 计算两点距离。
	 *
	 * @param first 第一个点。
	 * @param second 第二个点。
	 * @returns 欧氏距离。
	 */
	public distance(first: WorldCoordinate, second: WorldCoordinate): number {
		return this.length(this.subtract(first, second));
	}

	/**
	 * 向量相减。
	 *
	 * @param first 被减向量。
	 * @param second 减向量。
	 * @returns 差值向量。
	 */
	public subtract(
		first: WorldCoordinate | TopologyFittingVectorTuple,
		second: WorldCoordinate | TopologyFittingVectorTuple,
	): TopologyFittingVectorTuple {
		return [
			first[0] - second[0],
			first[1] - second[1],
			first[2] - second[2],
		];
	}

	/**
	 * 按两个轴组合世界向量。
	 *
	 * @param xAxis 世界 X 轴。
	 * @param yAxis 世界 Y 轴。
	 * @param x x 系数。
	 * @param y y 系数。
	 * @returns 世界向量。
	 */
	public addScaled(
		xAxis: TopologyFittingVectorTuple,
		yAxis: TopologyFittingVectorTuple,
		x: number,
		y: number,
	): TopologyFittingVectorTuple {
		return [
			xAxis[0] * x + yAxis[0] * y,
			xAxis[1] * x + yAxis[1] * y,
			xAxis[2] * x + yAxis[2] * y,
		];
	}

	/**
	 * 向量缩放。
	 *
	 * @param vector 输入向量。
	 * @param scalar 缩放系数。
	 * @returns 缩放后向量。
	 */
	public scale(vector: TopologyFittingVectorTuple, scalar: number): TopologyFittingVectorTuple {
		return [vector[0] * scalar, vector[1] * scalar, vector[2] * scalar];
	}

	/**
	 * 向量点积。
	 *
	 * @param first 第一个向量。
	 * @param second 第二个向量。
	 * @returns 点积。
	 */
	public dot(first: TopologyFittingVectorTuple, second: TopologyFittingVectorTuple): number {
		return first[0] * second[0] + first[1] * second[1] + first[2] * second[2];
	}

	/**
	 * 向量叉积。
	 *
	 * @param first 第一个向量。
	 * @param second 第二个向量。
	 * @returns 叉积向量。
	 */
	public cross(
		first: TopologyFittingVectorTuple,
		second: TopologyFittingVectorTuple,
	): TopologyFittingVectorTuple {
		return [
			first[1] * second[2] - first[2] * second[1],
			first[2] * second[0] - first[0] * second[2],
			first[0] * second[1] - first[1] * second[0],
		];
	}

	/**
	 * 向量长度。
	 *
	 * @param vector 输入向量。
	 * @returns 长度。
	 */
	public length(vector: TopologyFittingVectorTuple): number {
		return Math.hypot(vector[0], vector[1], vector[2]);
	}

	/**
	 * 单位化向量。
	 *
	 * @param vector 输入向量。
	 * @returns 单位向量。
	 */
	public normalize(vector: TopologyFittingVectorTuple): TopologyFittingVectorTuple {
		const length = this.length(vector);
		return length > this.epsilon
			? this.scale(vector, 1 / length)
			: [1, 0, 0];
	}

	/**
	 * 解析拟合平面的优先 X 轴。
	 *
	 * @param points 世界坐标采样点。
	 * @param origin 平面原点。
	 * @returns 优先方向。
	 */
	private resolvePreferredPlaneXAxis(
		points: WorldCoordinate[],
		origin: TopologyFittingVectorTuple,
	): TopologyFittingVectorTuple {
		const first = points[0]!;
		const last = points[points.length - 1]!;
		const endAxis = this.subtract(last, first);
		if (this.length(endAxis) > this.epsilon) {
			return this.normalize(endAxis);
		}

		let bestAxis = this.subtract(first, origin);
		points.forEach((point) => {
			const candidate = this.subtract(point, origin);
			if (this.length(candidate) > this.length(bestAxis)) {
				bestAxis = candidate;
			}
		});

		return this.length(bestAxis) > this.epsilon
			? this.normalize(bestAxis)
			: [1, 0, 0];
	}

	/**
	 * 创建二维局部点集归一化参数。
	 *
	 * @param points 局部二维采样点。
	 * @returns 平移原点和等比缩放系数。
	 */
	private createLocalPointNormalization(points: TopologyFittingLocalPoint2[]): {
		origin: TopologyFittingLocalPoint2;
		scale: number;
	} | null {
		if (points.length === 0) {
			return null;
		}

		const origin = points.reduce<TopologyFittingLocalPoint2>(
			(accumulator, point) => ({
				x: accumulator.x + point.x,
				y: accumulator.y + point.y,
			}),
			{x: 0, y: 0},
		);
		origin.x /= points.length;
		origin.y /= points.length;

		const maxExtent = Math.max(...points.map((point) => (
			Math.hypot(point.x - origin.x, point.y - origin.y)
		)));
		if (maxExtent <= this.epsilon) {
			return null;
		}

		return {
			origin,
			scale: 1 / maxExtent,
		};
	}

	/**
	 * 应用二维局部点集归一化参数。
	 *
	 * @param point 原始局部点。
	 * @param normalization 归一化参数。
	 * @returns 归一化后的局部点。
	 */
	private normalizeLocalPoint(
		point: TopologyFittingLocalPoint2,
		normalization: {origin: TopologyFittingLocalPoint2; scale: number},
	): TopologyFittingLocalPoint2 {
		return {
			x: (point.x - normalization.origin.x) * normalization.scale,
			y: (point.y - normalization.origin.y) * normalization.scale,
		};
	}

	/**
	 * 解最小二乘线性系统。
	 *
	 * @param rows 系数矩阵行。
	 * @param values 右侧向量。
	 * @returns 解向量。
	 */
	private solveLeastSquares(rows: number[][], values: number[]): number[] | null {
		if (rows.length === 0 || rows[0]?.length === 0) {
			return null;
		}

		const columnCount = rows[0].length;
		const matrix = Array.from({length: columnCount}, () => Array.from({length: columnCount}, () => 0));
		const rhs = Array.from({length: columnCount}, () => 0);

		rows.forEach((row, rowIndex) => {
			for (let column = 0; column < columnCount; column += 1) {
				rhs[column] += row[column]! * values[rowIndex]!;
				for (let inner = 0; inner < columnCount; inner += 1) {
					matrix[column]![inner]! += row[column]! * row[inner]!;
				}
			}
		});

		return this.solveLinearSystem(matrix, rhs);
	}

	/**
	 * 高斯消元解线性系统。
	 *
	 * @param matrix 系数矩阵。
	 * @param rhs 右侧向量。
	 * @returns 解向量。
	 */
	private solveLinearSystem(matrix: number[][], rhs: number[]): number[] | null {
		const size = rhs.length;
		const augmented = matrix.map((row, index) => [...row, rhs[index]!]);

		for (let pivotIndex = 0; pivotIndex < size; pivotIndex += 1) {
			let bestRow = pivotIndex;
			for (let row = pivotIndex + 1; row < size; row += 1) {
				if (Math.abs(augmented[row]![pivotIndex]!) > Math.abs(augmented[bestRow]![pivotIndex]!)) {
					bestRow = row;
				}
			}

			if (Math.abs(augmented[bestRow]![pivotIndex]!) <= this.epsilon) {
				return null;
			}

			[augmented[pivotIndex], augmented[bestRow]] = [augmented[bestRow]!, augmented[pivotIndex]!];
			const pivot = augmented[pivotIndex]![pivotIndex]!;
			for (let column = pivotIndex; column <= size; column += 1) {
				augmented[pivotIndex]![column]! /= pivot;
			}

			for (let row = 0; row < size; row += 1) {
				if (row === pivotIndex) {
					continue;
				}
				const factor = augmented[row]![pivotIndex]!;
				for (let column = pivotIndex; column <= size; column += 1) {
					augmented[row]![column]! -= factor * augmented[pivotIndex]![column]!;
				}
			}
		}

		return augmented.map((row) => row[size]!);
	}

	/**
	 * 解析二维对称矩阵特征向量。
	 *
	 * @param a 矩阵 xx 项。
	 * @param offDiagonal 矩阵 xy 项。
	 * @param c 矩阵 yy 项。
	 * @returns 两个特征值与单位特征向量。
	 */
	private resolveSymmetricEigen(
		a: number,
		offDiagonal: number,
		c: number,
	): {
		firstValue: number;
		secondValue: number;
		firstVector: TopologyFittingLocalPoint2;
		secondVector: TopologyFittingLocalPoint2;
	} | null {
		const trace = a + c;
		const delta = Math.sqrt((a - c) * (a - c) + 4 * offDiagonal * offDiagonal);
		const firstValue = (trace + delta) / 2;
		const secondValue = (trace - delta) / 2;
		if (Math.abs(offDiagonal) <= this.epsilon) {
			// 对角矩阵必须显式返回两条正交坐标轴；若分别调用通用公式，零交叉项会让两次求解落到同一轴。
			const firstVector = a >= c ? {x: 1, y: 0} : {x: 0, y: 1};
			const secondVector = a >= c ? {x: 0, y: 1} : {x: 1, y: 0};
			return {firstValue, secondValue, firstVector, secondVector};
		}
		const firstVector = this.resolveEigenVector(a, offDiagonal, c, firstValue);
		const secondVector = this.resolveEigenVector(a, offDiagonal, c, secondValue);
		return firstVector && secondVector
			? {firstValue, secondValue, firstVector, secondVector}
			: null;
	}

	/**
	 * 解析二维矩阵单个特征向量。
	 *
	 * @param a 矩阵 xx 项。
	 * @param offDiagonal 矩阵 xy 项。
	 * @param c 矩阵 yy 项。
	 * @param value 特征值。
	 * @returns 单位特征向量。
	 */
	private resolveEigenVector(
		a: number,
		offDiagonal: number,
		c: number,
		value: number,
	): TopologyFittingLocalPoint2 | null {
		const firstCandidate = {x: value - c, y: offDiagonal};
		const secondCandidate = {x: offDiagonal, y: value - a};
		// 两个候选都满足特征方程，优先使用模长较大者以降低近对角矩阵的归一化误差。
		const vector = Math.hypot(firstCandidate.x, firstCandidate.y)
			>= Math.hypot(secondCandidate.x, secondCandidate.y)
			? firstCandidate
			: secondCandidate;
		const length = Math.hypot(vector.x, vector.y);
		return length > this.epsilon
			? {x: vector.x / length, y: vector.y / length}
			: null;
	}

	/**
	 * 计算 conic 方程值。
	 *
	 * @param conic conic 系数。
	 * @param point 局部点。
	 * @returns 方程值。
	 */
	private evaluateConic(
		conic: TopologyUnitConicCoefficients,
		point: TopologyFittingLocalPoint2,
	): number {
		return conic.a * point.x * point.x
			+ conic.b * point.x * point.y
			+ conic.c * point.y * point.y
			+ conic.d * point.x
			+ conic.e * point.y
			+ conic.f;
	}

	/**
	 * 展开参数角序列。
	 *
	 * @param angles 原始角度序列。
	 * @returns 起点归零后的终止角和方向。
	 */
	private unwrapAngleTrace(angles: number[]): TopologyAngleTraceResult {
		if (angles.length < 2) {
			return {
				startAngle: angles[0] ?? 0,
				endAngle: angles[0] ?? 0,
				clockwise: false,
			};
		}

		const startAngle = angles[0]!;
		let total = 0;
		for (let index = 1; index < angles.length; index += 1) {
			let delta = angles[index]! - angles[index - 1]!;
			while (delta > Math.PI) {
				delta -= Math.PI * 2;
			}
			while (delta < -Math.PI) {
				delta += Math.PI * 2;
			}
			total += delta;
		}
		return {
			startAngle,
			endAngle: startAngle + total,
			clockwise: total < 0,
		};
	}

	/**
	 * 计算点集质心。
	 *
	 * @param points 世界坐标点。
	 * @returns 质心。
	 */
	private centroid(points: WorldCoordinate[]): TopologyFittingVectorTuple {
		const sum = points.reduce<TopologyFittingVectorTuple>(
			(accumulator, point) => [
				accumulator[0] + point[0],
				accumulator[1] + point[1],
				accumulator[2] + point[2],
			],
			[0, 0, 0],
		);
		return this.scale(sum, 1 / points.length);
	}
}
